/**
 * Push Notification Service
 *
 * Handles service-worker registration, push subscription, and notification permission.
 * The VAPID public key comes from REACT_APP_VAPID_PUBLIC_KEY env var.
 */

import backendClient from './backendClient';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// ─── helpers ──────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// ─── service-worker registration ──────────────────────────────

let swRegistration = null;

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;

  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js');
    return swRegistration;
  } catch (err) {
    console.error('SW registration failed:', err);
    return null;
  }
}

export function getRegistration() {
  return swRegistration;
}

// ─── push subscription ───────────────────────────────────────

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && !!VAPID_PUBLIC_KEY;
}

export async function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/**
 * Subscribe the current browser to push notifications.
 * Sends the PushSubscription to the backend.
 * Returns the subscription object on success, null on failure.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const reg = swRegistration || (await registerServiceWorker());
  if (!reg) return null;

  try {
    // Check for existing subscription first
    let subscription = await reg.pushManager.getSubscription();

    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    // Send subscription to backend
    await backendClient.post('/api/push/subscribe', {
      subscription: subscription.toJSON(),
    });

    return subscription;
  } catch (err) {
    console.error('Push subscription failed:', err);
    return null;
  }
}

/**
 * Unsubscribe from push notifications and tell the backend.
 */
export async function unsubscribeFromPush() {
  const reg = swRegistration || (await navigator.serviceWorker?.ready);
  if (!reg) return;

  try {
    const subscription = await reg.pushManager.getSubscription();
    if (subscription) {
      await backendClient.post('/api/push/unsubscribe', {
        endpoint: subscription.endpoint,
      });
      await subscription.unsubscribe();
    }
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
  }
}

/**
 * Check whether the current browser is already subscribed.
 */
export async function isSubscribed() {
  const reg = swRegistration || (await navigator.serviceWorker?.ready);
  if (!reg) return false;

  const subscription = await reg.pushManager.getSubscription();
  return !!subscription;
}
